import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PermissionAwareState } from '@/components/participants/schedule/PermissionAwareState';

/**
 * AssessmentsIndexTab — unified read-INDEX over existing authoritative sources.
 *
 * Sources aggregated (read-only, participant-filtered):
 *   - Risk Assessments   (/api/risk/by-participant/<id>)
 *   - Care Plan sections (extracted from /api/careplans/by-participant/<id>)
 *   - Assessment docs    (/api/participants/<id>/documents filtered by category)
 *
 * No new collection. No writes. No copies. Every row keeps its source label so
 * users can trace back to the authoritative module. Clinical-only assessments
 * remain protected by the destination endpoint's permission — this INDEX does
 * NOT bypass RBAC.
 */

const ASSESSMENT_DOC_CATEGORIES = new Set([
  'BEHAVIOUR_SUPPORT_PLAN',
  'MEDICAL',
  'RISK_ASSESSMENT_DOC',
  'GUARDIAN_DOCUMENT',
]);

// Care Plan section titles that qualify as assessment-adjacent (audit-driven).
const ASSESSMENT_SECTION_KEYS = new Set([
  'personal_care_needs',
  'mobility_support',
  'behaviour_support_strategies',
  'communication_preferences',
  'medical_alerts',
  'dietary_requirements',
]);

export function AssessmentsIndexTab({ participantId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchState, setFetchState] = useState('ok');

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setFetchState('ok');

    const safeGet = (url) =>
      api.get(url)
        .then((r) => r?.data)
        .catch((e) => {
          const status = e?.response?.status ?? null;
          console.error('[AssessmentsIndexTab] source failed', { url, httpStatus: status });
          return { __denied: status === 401 || status === 403, __error: !(status === 401 || status === 403) };
        });

    Promise.all([
      safeGet(`/risk/by-participant/${participantId}`),
      safeGet(`/careplans/by-participant/${participantId}`),
      safeGet(`/participants/${participantId}/documents`),
    ])
      .then(([riskResp, planResp, docsResp]) => {
        if (cancelled) return;
        const allDenied = [riskResp, planResp, docsResp].every((r) => r && r.__denied);
        const allError = [riskResp, planResp, docsResp].every((r) => r && (r.__denied || r.__error));
        if (allDenied) { setFetchState('denied'); setItems([]); return; }

        const merged = [];

        // Risk Assessments
        const risks = riskResp?.data || riskResp?.items || [];
        (Array.isArray(risks) ? risks : []).forEach((r) => {
          merged.push({
            key: `risk:${r.id || r._id}`,
            sourceType: 'Risk Assessment',
            sourceModule: 'Risk & Safeguarding',
            title: r.title || r.risk_type || 'Risk Assessment',
            status: r.status || '—',
            completionDate: r.assessedAt || r.created_at || '—',
            reviewDate: r.review_date || r.reviewDate || '—',
            author: r.assessed_by || r.author || '—',
            deepLink: { tab: 'risk', sub: 'risks' },
          });
        });

        // Care Plan sections — only surfaces section metadata, NOT content
        const plans = planResp?.data || planResp?.items || planResp || [];
        (Array.isArray(plans) ? plans : []).forEach((p) => {
          const sections = p.sections || p.structured_sections || {};
          Object.keys(sections).forEach((sk) => {
            if (!ASSESSMENT_SECTION_KEYS.has(sk)) return;
            const section = sections[sk] || {};
            merged.push({
              key: `careplan-section:${p.id || p._id}:${sk}`,
              sourceType: 'Care Plan Section',
              sourceModule: 'Care & Support',
              title: section.title || sk.replace(/_/g, ' '),
              status: p.status || '—',
              completionDate: p.updated_at || p.created_at || '—',
              reviewDate: p.review_due_date || '—',
              author: p.created_by_user_id || '—',
              deepLink: { tab: 'care-support', sub: 'careplans' },
            });
          });
        });

        // Assessment documents
        const docs = docsResp?.data || docsResp?.items || [];
        (Array.isArray(docs) ? docs : []).forEach((d) => {
          if (!ASSESSMENT_DOC_CATEGORIES.has(d.category)) return;
          merged.push({
            key: `document:${d.id || d._id}`,
            sourceType: `Document (${d.category})`,
            sourceModule: 'Documents',
            title: d.title || d.filename || 'Assessment document',
            status: d.status || '—',
            completionDate: d.documentDate || d.effectiveDate || d.createdAt || '—',
            reviewDate: d.reviewDate || d.expiryDate || '—',
            author: d.uploadedBy || '—',
            deepLink: { tab: 'notes-docs', sub: 'documents' },
          });
        });

        setItems(merged);
        if (merged.length === 0 && allError) setFetchState('error');
        else if (merged.length === 0) setFetchState('empty');
        else setFetchState('ok');
      })
      .catch(() => {
        if (!cancelled) setFetchState('error');
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [participantId]);

  useEffect(() => {
    const cleanup = load();
    return cleanup;
  }, [load]);

  if (loading) {
    return (
      <div className="p-6 text-sm text-muted-foreground" role="status" aria-live="polite" data-testid="assessments-loading">
        Loading assessments…
      </div>
    );
  }
  if (fetchState === 'denied') {
    return <PermissionAwareState status="denied" testIdBase="assessments" />;
  }
  if (fetchState === 'error') {
    return <PermissionAwareState status="error" testIdBase="assessments" onRetry={load} />;
  }
  if (fetchState === 'empty') {
    return (
      <PermissionAwareState
        status="empty"
        testIdBase="assessments"
        emptyText="No assessment records found for this participant."
      />
    );
  }

  return (
    <div className="space-y-3" data-testid="assessments-tab">
      <h3 className="text-sm font-semibold" data-testid="assessments-count">
        Assessment Records ({items.length})
      </h3>
      <p className="text-[11px] text-muted-foreground italic">
        Unified read-index — every row links back to its authoritative module. This surface is
        navigation-only and does not modify any source record.
      </p>
      <div className="space-y-2">
        {items.map((it) => (
          <Card key={it.key} data-testid={`assessments-row-${it.key}`}>
            <CardContent className="py-3 flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className="text-[10px]">{it.sourceType}</Badge>
                  <span className="text-xs text-slate-500">{it.sourceModule}</span>
                </div>
                <p className="text-sm font-medium mt-1 truncate">{it.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5 truncate">
                  Completion: {it.completionDate} · Review: {it.reviewDate} · Author: {it.author}
                </p>
              </div>
              <Badge variant="outline" className="text-[10px]">{it.status}</Badge>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

export default AssessmentsIndexTab;
